import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  Rating,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Drawer,
  IconButton,
  Badge,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  Breadcrumbs,
  Link,
  Skeleton,
  Alert,
  Tooltip,
  Dialog,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Search,
  FilterList,
  GridView,
  ViewList,
  ShoppingCart,
  FavoriteBorder,
  Favorite,
  Clear,
  Home,
  Category,
  ChevronLeft,
  ChevronRight,
  ExpandMore,
} from '@mui/icons-material';
import { useSearchParams, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';

import axios from 'axios';
import PriceRangeFilter, { PRICE_MIN, PRICE_MAX } from '../components/products/PriceRangeFilter';
import QuantityRangeFilter, { QUANTITY_MIN, QUANTITY_MAX } from '../components/products/QuantityRangeFilter';

import { fetchProducts, fetchFeaturedProducts, setFilters, setViewMode, setCurrentPage } from '../store/slices/productSlice';
import { addToCart } from '../store/slices/cartSlice';
import { addToWishlist, removeFromWishlist, fetchCustomerWishlist } from '../store/customerSlice';
import VisitRequestDialog from '../components/VisitRequestDialog';
import BookLotDialog from '../components/product/BookLotDialog';
import { visitRequestService } from '../services/visitRequestService';
import lotRequestService from '../services/lotRequestService';
import { getBestPromotionForProduct } from '../utils/promotionUtils';
import { productService } from '../services/productService';
import { getProductPrimaryImageUrl } from '../utils/imageUtils';

const ProductCard = React.memo(
  ({ product, isInWishlist, onWishlistToggle, onRequestVisit, onBookLot, onAddToCart, allProducts = [] }) => {
    const promotion = getBestPromotionForProduct(product.id, product.category, allProducts);
    const [isHovered, setIsHovered] = React.useState(false);
    
    // Calculate price
    const basePrice = Number(product.price);
    let finalPrice = product.discountedPrice || basePrice;
    if (promotion) {
      if (promotion.discountType === 'percentage') {
        const discountPercent = Number(promotion.discount) || 0;
        finalPrice = basePrice * (1 - discountPercent / 100);
      } else if (promotion.discountType === 'amount') {
        const discountAmount = Number(promotion.discountAmount) || 0;
        finalPrice = Math.max(0, basePrice - discountAmount);
      }
    }
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={() => window.open(`/products/${product.id}`, '_blank')}
          sx={{
            height: '320px',
            width: '320px',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 2,
            bgcolor: 'white',
            border: 'none',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
              transform: 'translateY(-4px)',
              '& .product-image': {
                transform: 'scale(1.1)',
              },
            },
          }}
        >
          {/* Image Container - Full Height */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            {getProductPrimaryImageUrl(product, false, 'medium') ? (
              <CardMedia
                component="img"
                className="product-image"
                image={getProductPrimaryImageUrl(product, false, 'medium')}
                alt={product.name}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.5s ease-in-out',
                }}
                onError={(e) => {
                  // If image fails to load, show mos-logo.jpg in grayscale
                  e.target.src = '/mos-logo.jpg';
                  e.target.style.filter = 'grayscale(100%)';
                }}
              />
            ) : (
              <CardMedia
                component="img"
                className="product-image"
                image="/mos-logo.jpg"
                alt={product.name}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: 'grayscale(100%)',
                  opacity: 0.7,
                  transition: 'transform 0.5s ease-in-out',
                }}
              />
            )}

            {/* Top Badges - Always Visible */}
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                right: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                zIndex: 3,
              }}
            >
              {product.featured && (
                <Chip
                  label="Featured"
                  color="secondary"
                  size="small"
                  sx={{
                    backdropFilter: 'blur(4px)',
                    bgcolor: '#FFDB58',
                  }}
                />
              )}
              {promotion && (
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                    borderRadius: '20px',
                    px: 1.5,
                    py: 0.5,
                    boxShadow: '0 4px 12px rgba(255, 107, 107, 0.4)',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'white',
                      fontWeight: 800,
                      fontSize: '0.7rem',
                      letterSpacing: '0.5px',
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: '0.85rem',
                        fontWeight: 900,
                      }}
                    >
                      {promotion.discountType === 'amount' ? '₹' : ''}
                      {promotion.discountType === 'amount'
                        ? promotion.discountAmount || 0
                        : promotion.discount || 0}
                      {promotion.discountType === 'percentage' ? '%' : ''}
                    </Box>
                    <Box component="span" sx={{ fontSize: '0.65rem', opacity: 0.95 }}>
                      OFF
                    </Box>
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Wishlist Button - Always Visible */}
            <IconButton
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(4px)',
                zIndex: 3,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,1)',
                  transform: 'scale(1.1)',
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                onWishlistToggle(product, isInWishlist);
              }}
            >
              {isInWishlist ? <Favorite color="error" /> : <FavoriteBorder />}
            </IconButton>

            {/* Bottom Name & Price - Visible when not hovered */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)',
                padding: '16px 14px 14px',
                zIndex: 2,
                textAlign: 'center',
                opacity: isHovered ? 0 : 1,
                transition: 'opacity 0.3s ease-in-out',
                pointerEvents: isHovered ? 'none' : 'auto',
              }}
            >
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '24px',
                  mb: 0.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.3,
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                }}
              >
                {product.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '16px' }}>
                  ₹{finalPrice.toFixed(2)}
                </Typography>
                {(product.discountedPrice || promotion) && basePrice > finalPrice && (
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      textDecoration: 'line-through',
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '14px',
                    }}
                  >
                    ₹{basePrice.toFixed(2)}
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem' }}>
                {product.priceUnit || 'per unit'} +GST
              </Typography>
            </Box>

            {/* Hover Overlay - Product Details */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.9) 30%, rgba(0,0,0,0.7) 60%, transparent 100%)',
                padding: '16px 14px',
                transform: isHovered ? 'translateY(0)' : 'translateY(100%)',
                transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                zIndex: 4,
                maxHeight: '90%',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(255,255,255,0.3)',
                  borderRadius: '3px',
                },
              }}
            >
              {/* Product Name in Overlay */}
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '20px',
                  mb: 1,
                  textAlign: 'center',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {product.name}
              </Typography>

              {/* Category Chips */}
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Chip
                  label={product.category}
                  size="small"
                  sx={{
                    fontSize: '10px',
                    height: '24px',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.3)',
                  }}
                />
                {product.origin && (
                  <Chip
                    label={product.origin}
                    size="small"
                    sx={{
                      fontSize: '10px',
                      height: '24px',
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.3)',
                    }}
                  />
                )}
              </Box>

              {/* Specifications */}
              <Box sx={{ mb: 1 }}>
                {(product.specifications?.color || product.specifications?.finish) && (
                  <Box sx={{ display: 'flex', gap: 0.75, mb: 0.75, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                    {product.specifications?.color && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            bgcolor: product.specifications.color.toLowerCase(),
                            border: '2px solid rgba(255,255,255,0.5)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          }}
                        />
                        <Typography variant="caption" sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)' }}>
                          {product.specifications.color}
                        </Typography>
                      </Box>
                    )}
                    {product.specifications?.finish && (
                      <Typography variant="caption" sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)' }}>
                        • {product.specifications.finish}
                      </Typography>
                    )}
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.75 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: product.stockQuantity > 0 ? '#4caf50' : '#f44336',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                  />
                  <Typography variant="caption" sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.9)' }}>
                    {product.stockQuantity > 0 ? `${product.stockQuantity} in stock` : 'Out of stock'}
                  </Typography>
                </Box>

                {product.specifications?.thickness && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '11px',
                      color: 'rgba(255,255,255,0.9)',
                      display: 'block',
                      textAlign: 'center',
                      mb: 0.5,
                    }}
                  >
                    Thickness: {product.specifications.thickness}
                  </Typography>
                )}
              </Box>

              {/* Price in Overlay */}
              <Box sx={{ mb: 1.5, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.5 }}>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '18px' }}>
                    ₹{finalPrice.toFixed(2)}
                  </Typography>
                  {(product.discountedPrice || promotion) && basePrice > finalPrice && (
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        textDecoration: 'line-through',
                        color: 'rgba(255,255,255,0.6)',
                        fontSize: '12px',
                      }}
                    >
                      ₹{basePrice.toFixed(2)}
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem' }}>
                  {product.priceUnit || 'per unit'} +GST
                </Typography>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  <Tooltip title="Schedule a visit to view this product in person" arrow>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{
                        textTransform: 'none',
                        fontSize: '10px',
                        py: 0.5,
                        fontWeight: 600,
                        borderColor: 'rgba(255,255,255,0.5)',
                        color: 'white',
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: 'rgba(255,255,255,0.1)',
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRequestVisit(product);
                      }}
                    >
                      Request Visit
                    </Button>
                  </Tooltip>
                  <Tooltip title="Reserve a bulk quantity of this stone for your project." arrow>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{
                        textTransform: 'none',
                        fontSize: '10px',
                        py: 0.5,
                        fontWeight: 600,
                        borderColor: 'rgba(255,255,255,0.5)',
                        color: 'white',
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: 'rgba(255,255,255,0.1)',
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onBookLot(product);
                      }}
                    >
                      BOOK A LOT
                    </Button>
                  </Tooltip>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<ShoppingCart />}
                  sx={{
                    bgcolor: '#2c3e50',
                    color: 'white',
                    textTransform: 'none',
                    fontSize: '12px',
                    fontWeight: 600,
                    py: 1,
                    '&:hover': {
                      bgcolor: '#34495e',
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddToCart(product);
                  }}
                >
                  ADD TO CART
                </Button>
              </Box>
            </Box>
          </Box>
        </Card>
      </motion.div>
    );
  }
);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation(); // Get location to track URL changes
  
  const {
    products: generalProducts,
    loading: generalLoading,
    error: generalError,
    featuredProducts,
    featuredLoading,
    filters,
    viewMode,
    currentPage,
    totalPages,
    totalElements,
  } = useSelector((state) => state.products);
  
  const { wishlist } = useSelector((state) => state.customer);
  const { user } = useSelector((state) => state.auth || {});
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const isSeller = user?.role === 'SELLER';
  
  // Check if featured products are requested
  const featured = searchParams.get('featured') === 'true';
  
  // AI Search state - Declare BEFORE using it
  const [aiSearchResults, setAiSearchResults] = useState(null);
  const [aiSearchLoading, setAiSearchLoading] = useState(false);
  const [aiSearchError, setAiSearchError] = useState(null);
  const [aiSearchMessage, setAiSearchMessage] = useState(null);
  
  // Use AI search results if available, otherwise use regular products
  const displayProducts = aiSearchResults?.main_product || (featured ? featuredProducts : generalProducts);
  const products = displayProducts;
  const loading = aiSearchLoading || (featured ? featuredLoading : generalLoading);
  const error = aiSearchError || generalError;

  const effectiveTotalPages = totalPages;
  const effectiveTotalElements = totalElements;
  const effectiveCurrentPage = currentPage;
  
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [localFilters, setLocalFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    selectedCategories: searchParams.get('category') ? searchParams.get('category').split(',') : [],
    priceRange: [PRICE_MIN, PRICE_MAX], // Full range - won't filter by default
    quantityRange: [QUANTITY_MIN, QUANTITY_MAX], // Full range - won't filter by default
    material: '',
    finish: '',
    origin: '',
    status: '',
    approvalStatus: '',
    discountFilter: '',
    sortBy: 'createdAt',
    sortDir: 'desc',
  });
  
  // Prices of all products matching the current non-price filters, for the live "N products in range" count
  const [priceCountParams, setPriceCountParams] = useState(null);
  const [priceList, setPriceList] = useState(null);
  const [priceListLoading, setPriceListLoading] = useState(false);

  // Track if user has explicitly set price range (to distinguish from auto-calculated range)
  const [priceRangeExplicitlySet, setPriceRangeExplicitlySet] = useState(false);

  // Same as the price list above, for the Quantity filter: backend-calculated availableSft of all
  // products matching the current non-quantity filters
  const [quantityCountParams, setQuantityCountParams] = useState(null);
  const [quantityList, setQuantityList] = useState(null);
  const [quantityListLoading, setQuantityListLoading] = useState(false);
  const [quantityRangeExplicitlySet, setQuantityRangeExplicitlySet] = useState(false);

  // Price and quantity counts usually request the same list (no price/quantity filter set); share it
  const countListCache = useRef(new Map());
  const loadCountList = useCallback((paramsKey) => {
    const cache = countListCache.current;
    if (!cache.has(paramsKey)) {
      if (cache.size > 20) cache.clear();
      const request = axios.get(`${API_BASE_URL}/products`, { params: { ...JSON.parse(paramsKey), page: 0, size: 10000 } })
        .then((res) => res.data?.content || res.data || []);
      request.catch(() => cache.delete(paramsKey));
      cache.set(paramsKey, request);
    }
    return cache.get(paramsKey);
  }, []);
  
  // Debounce timer for search input
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);
  
  // Visit request dialog state
  const [visitDialogOpen, setVisitDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Book a lot dialog state
  const [bookLotDialogOpen, setBookLotDialogOpen] = useState(false);
  const [selectedLotProduct, setSelectedLotProduct] = useState(null);

  // Fixed slider bounds: 0 to 5,000
  const sliderBounds = useMemo(() => {
    return {
      min: 0,
      max: PRICE_MAX,
    };
  }, []);

  const boundedPriceRange = useMemo(() => {
    // If priceRange is null or not set, use the full slider bounds (no filter applied)
    // (the thumbs follow localFilters.priceRange while dragging; the default is the full range)
    if (!localFilters.priceRange) {
      return [sliderBounds.min, sliderBounds.max];
    }
    const [currentMin = sliderBounds.min, currentMax = sliderBounds.max] = localFilters.priceRange;
    const clampedMin = Math.min(Math.max(currentMin, sliderBounds.min), sliderBounds.max);
    const clampedMax = Math.max(Math.min(currentMax, sliderBounds.max), clampedMin);
    return [clampedMin, clampedMax];
  }, [localFilters.priceRange, sliderBounds.max, sliderBounds.min]);

  const boundedQuantityRange = useMemo(() => {
    if (!localFilters.quantityRange) {
      return [QUANTITY_MIN, QUANTITY_MAX];
    }
    const [currentMin = QUANTITY_MIN, currentMax = QUANTITY_MAX] = localFilters.quantityRange;
    const clampedMin = Math.min(Math.max(currentMin, QUANTITY_MIN), QUANTITY_MAX);
    const clampedMax = Math.max(Math.min(currentMax, QUANTITY_MAX), clampedMin);
    return [clampedMin, clampedMax];
  }, [localFilters.quantityRange]);

  // Load the prices of every product matching the current non-price filters (see countParams in the
  // fetch effect). The displayed products are already price-filtered, so they can't give a live count.
  useEffect(() => {
    if (!priceCountParams) return;
    let cancelled = false;
    setPriceListLoading(true);
    loadCountList(priceCountParams)
      .then((list) => {
        if (cancelled) return;
        // Same price the backend filters on: discountedPrice when set, otherwise price
        setPriceList(list.map((p) => Number(p.discountedPrice ?? p.price)).filter(Number.isFinite));
      })
      .catch(() => { if (!cancelled) setPriceList(null); })
      .finally(() => { if (!cancelled) setPriceListLoading(false); });
    return () => { cancelled = true; };
  }, [priceCountParams, loadCountList]);

  useEffect(() => {
    if (!quantityCountParams) return;
    let cancelled = false;
    setQuantityListLoading(true);
    loadCountList(quantityCountParams)
      .then((list) => {
        if (cancelled) return;
        // availableSft is calculated by the backend (never in the frontend)
        setQuantityList(list.map((p) => Number(p.availableSft)).filter(Number.isFinite));
      })
      .catch(() => { if (!cancelled) setQuantityList(null); })
      .finally(() => { if (!cancelled) setQuantityListLoading(false); });
    return () => { cancelled = true; };
  }, [quantityCountParams, loadCountList]);

  // AI search / featured views don't go through the regular fetch, so count from what's shown
  const usesRegularProductFetch = !searchParams.get('ai_search') && searchParams.get('featured') !== 'true';
  const priceFilterPrices = useMemo(() => {
    if (usesRegularProductFetch && priceList) return priceList;
    return (products || []).map((p) => Number(p.discountedPrice ?? p.price)).filter(Number.isFinite);
  }, [usesRegularProductFetch, priceList, products]);
  const quantityFilterValues = useMemo(() => {
    if (usesRegularProductFetch && quantityList) return quantityList;
    return (products || []).map((p) => Number(p.availableSft)).filter(Number.isFinite);
  }, [usesRegularProductFetch, quantityList, products]);

  // Shared category normalization function - ensures consistent category formatting
  const normalizeCategory = useCallback((cat) => {
    if (!cat) return '';
    const trimmed = cat.trim();
    if (!trimmed) return '';
    // Handle multi-word categories like "Natural Stone" - capitalize first letter of each word
    return trimmed
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }, []);

  // Parse and normalize category from URL param or string
  const parseCategory = useCallback((categoryParam) => {
    if (!categoryParam) return { categoryArray: [], category: '' };
    const categoryArray = categoryParam
      .split(',')
      .map(c => normalizeCategory(c))
      .filter(c => c);
    const category = categoryArray.length > 0 ? categoryArray.join(',') : '';
    return { categoryArray, category };
  }, [normalizeCategory]);

  // Set default priceRange to the full range when component mounts
  useEffect(() => {
    if (!priceRangeExplicitlySet && !localFilters.priceRange) {
      setLocalFilters((prev) => {
        if (!prev.priceRange) {
          return { ...prev, priceRange: [PRICE_MIN, PRICE_MAX] };
        }
        return prev;
      });
    }
  }, [priceRangeExplicitlySet]);
  
  // State to force re-render when promotions change
  const [promotionUpdateKey, setPromotionUpdateKey] = React.useState(0);
  
  // Listen for promotion changes to refresh product display
  useEffect(() => {
    const handlePromotionChange = () => {
      // Update key to force re-render of product cards
      setPromotionUpdateKey(prev => prev + 1);
    };
    
    window.addEventListener('promotionsUpdated', handlePromotionChange);
    
    return () => {
      window.removeEventListener('promotionsUpdated', handlePromotionChange);
    };
  }, []);
  
  // Read URL params directly from window.location to avoid timing issues with useSearchParams
  // This ensures we always get the current URL params, even on initial mount after navigation
  const getUrlParams = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      search: params.get('search') || '',
      category: params.get('category') || '',
    };
  }, []);

  // Compute effective filters from URL params (prioritized) or Redux filters
  // This ensures URL params are always used when available, fixing the race condition
  const effectiveFilters = useMemo(() => {
    // Read directly from window.location to ensure we get the latest URL params
    const urlParams = getUrlParams();
    const urlSearch = urlParams.search;
    const urlCategoryParam = urlParams.category;
    
    // Also try searchParams as fallback (for React Router updates)
    const routerSearch = searchParams.get('search') || '';
    const routerCategory = searchParams.get('category') || '';
    
    // Use window.location params first (most reliable), then router params, then Redux
    const finalSearch = urlSearch || routerSearch || '';
    const finalCategory = urlCategoryParam || routerCategory || '';
    
    // Normalize category from URL params using the same function
    const { category: normalizedUrlCategory } = parseCategory(finalCategory);
    
    // URL params take precedence - if they have a value (after trim), use them; otherwise use Redux filters
    const trimmedUrlSearch = finalSearch.trim();
    const trimmedUrlCategory = normalizedUrlCategory.trim();
    const effectiveSearch = trimmedUrlSearch || filters.search?.trim() || '';
    const effectiveCategory = trimmedUrlCategory || filters.category?.trim() || '';
    
    return {
      search: effectiveSearch,
      category: effectiveCategory,
    };
  }, [searchParams, filters.search, filters.category, parseCategory, getUrlParams]);

  // Track URL changes using React Router's location object
  // This ensures we detect all navigation changes including programmatic navigation
  const urlKey = useMemo(() => {
    // Create a key from the location to force effect re-run when URL changes
    return `${location.pathname}${location.search}`;
  }, [location.pathname, location.search]);

  // Handle AI Search
  useEffect(() => {
    const aiSearchQuery = searchParams.get('ai_search');
    if (aiSearchQuery) {
      setAiSearchLoading(true);
      setAiSearchError(null);
      setAiSearchMessage(null);
      
      productService.aiSearchProducts(aiSearchQuery)
        .then((response) => {
          setAiSearchResults(response);
          setAiSearchMessage(response.message || null);
          setAiSearchLoading(false);
          
          // If there are main products, convert them to the format expected by ProductsPage
          if (response.main_product && response.main_product.length > 0) {
            // Products are already in the correct format from AI service
            console.log('AI Search results:', response);
          } else if (response.message && !response.main_product) {
            // Unrelated query - show message
            console.log('AI Search message:', response.message);
          }
        })
        .catch((error) => {
          console.error('AI Search error:', error);
          setAiSearchError(error.message || 'Failed to perform AI search');
          setAiSearchLoading(false);
          // Fallback to regular search
          setSearchParams({ search: aiSearchQuery });
        });
    } else {
      // Clear AI search results when not using AI search
      setAiSearchResults(null);
      setAiSearchMessage(null);
      setAiSearchError(null);
    }
  }, [searchParams, setSearchParams]);
  
  // Load products on component mount and when filters change
  useEffect(() => {
    // Skip regular product fetching if AI search is active
    const aiSearchQuery = searchParams.get('ai_search');
    if (aiSearchQuery) {
      return;
    }
    
    // Check if featured products are requested
    const featured = searchParams.get('featured') === 'true';
    if (featured) {
      // Skip regular product fetching when featured products are requested
      return;
    }
    
    // Re-read URL params directly to ensure we have the latest values
    const currentUrlParams = getUrlParams();
    const urlSearch = currentUrlParams.search;
    const urlCategoryParam = currentUrlParams.category;
    
    // Normalize category from URL params
    const { category: normalizedUrlCategory } = parseCategory(urlCategoryParam);
    
    // Use URL params directly (most reliable), fallback to effectiveFilters
    const effectiveSearch = urlSearch.trim() || effectiveFilters.search || '';
    const effectiveCategory = normalizedUrlCategory.trim() || effectiveFilters.category || '';
    
    // Debug logging
    console.log('🔍 Product Fetch Effect:', {
      urlKey,
      urlSearch,
      urlCategoryParam,
      normalizedUrlCategory,
      effectiveSearch,
      effectiveCategory,
      reduxSearch: filters.search,
      reduxCategory: filters.category,
    });
    
    // GROUP retrieval: product_ids carries the exact IDs identified during autocomplete grouping
    const productIdsParam = searchParams.get('product_ids');
    const productIds = productIdsParam
      ? productIdsParam.split(',').map(s => s.trim()).filter(Boolean)
      : null;

    // Legacy: group_name for direct URL access / bookmarks
    const groupNameParam = searchParams.get('group_name');

    const isGroupRequest = (productIds && productIds.length > 0) || !!groupNameParam;

    // For all users (including sellers browsing the marketplace), use the general product endpoint
    const params = {
      // For group requests: always page 0, no extra filters
      page: isGroupRequest ? 0 : currentPage,
      size: isGroupRequest ? 10000 : 100,
      search: effectiveSearch || undefined,
      category: effectiveCategory || undefined,
      material: (filters.material || '').trim() || undefined,
      finish: (filters.finish || '').trim() || undefined,
      origin: (filters.origin || '').trim() || undefined,
      sortBy: filters.sortBy || 'createdAt',
      sortDir: filters.sortDir || 'desc',
      priceRange: filters.priceRange,
      productIds: productIds || undefined,
      groupName: groupNameParam || undefined,
    };

    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === '' || params[key] === null) {
        delete params[key];
      }
    });

    const isFullRange = filters.priceRange &&
      Array.isArray(filters.priceRange) &&
      filters.priceRange[0] <= PRICE_MIN &&
      filters.priceRange[1] >= PRICE_MAX;

    if (!priceRangeExplicitlySet || !filters.priceRange || isFullRange) {
      delete params.priceRange;
      delete params.minPrice;
      delete params.maxPrice;
    } else {
      // Send explicit bounds. The slider's top end means "₹5,000 and above", so no maxPrice there.
      const [lo, hi] = filters.priceRange;
      delete params.priceRange;
      params.minPrice = lo;
      if (hi < PRICE_MAX) params.maxPrice = hi;
    }

    // Quantity (available SFT) range; the slider's top end means "and above", so no maxQuantity there
    const quantityRange = filters.quantityRange;
    const isFullQuantityRange = !Array.isArray(quantityRange) ||
      (quantityRange[0] <= QUANTITY_MIN && quantityRange[1] >= QUANTITY_MAX);
    if (quantityRangeExplicitlySet && !isGroupRequest && !isFullQuantityRange) {
      const [lo, hi] = quantityRange;
      if (lo > QUANTITY_MIN) params.minQuantity = lo;
      if (hi < QUANTITY_MAX) params.maxQuantity = hi;
    }

    // Non-admin users (including sellers browsing) always see ACTIVE + APPROVED products
    if (!isAdmin) {
      params.status = 'ACTIVE';
      params.approvalStatus = 'APPROVED';
    }

    // Params for the price-count list: everything except price, paging and sorting
    const countParams = {};
    Object.entries(params).forEach(([key, val]) => {
      if (['priceRange', 'minPrice', 'maxPrice', 'page', 'size', 'sortBy', 'sortDir'].includes(key)) return;
      if (key === 'productIds') countParams.product_ids = val.join(',');
      else if (key === 'groupName') countParams.group_name = val;
      else countParams[key] = val;
    });
    setPriceCountParams(JSON.stringify(countParams));

    // Params for the quantity-count list: everything except quantity, paging and sorting
    const quantityCountParamsObj = {};
    Object.entries(params).forEach(([key, val]) => {
      if (['priceRange', 'minQuantity', 'maxQuantity', 'page', 'size', 'sortBy', 'sortDir'].includes(key)) return;
      if (key === 'productIds') quantityCountParamsObj.product_ids = val.join(',');
      else if (key === 'groupName') quantityCountParamsObj.group_name = val;
      else quantityCountParamsObj[key] = val;
    });
    setQuantityCountParams(JSON.stringify(quantityCountParamsObj));

    console.log('📦 Fetching products with params:', params);
    dispatch(fetchProducts(params));
  }, [dispatch, currentPage, filters, isAdmin, user?.role, searchParams, priceRangeExplicitlySet, quantityRangeExplicitlySet, effectiveFilters, urlKey, getUrlParams, parseCategory, setSearchParams]);
  
  // Debug: Log products data when it changes
  useEffect(() => {
    console.log('Products data received:', products);
    if (products.length > 0) {
      console.log('Sample product:', products[0]);
      console.log('Sample product image property:', products[0].image);
    }
  }, [products]);
  
  // Update local filters from URL params and sync with Redux
  // This effect runs when URL params change to ensure filters are initialized before product fetching
  useEffect(() => {
    // Read directly from window.location to get the most current URL params
    const urlParams = getUrlParams();
    const search = urlParams.search;
    const categoryParam = urlParams.category;
    
    // Also check searchParams for React Router updates
    const routerSearch = searchParams.get('search') || '';
    const routerCategory = searchParams.get('category') || '';
    
    // Use window.location params first (most reliable), then router params
    const finalSearch = search || routerSearch;
    const finalCategory = categoryParam || routerCategory;
    const featured = searchParams.get('featured') === 'true';
    
    // Use the shared parseCategory function for consistent normalization
    const { categoryArray, category } = parseCategory(finalCategory);
    
    // Update local filters immediately
    setLocalFilters(prev => ({
      ...prev,
      search: finalSearch,
      category,
      selectedCategories: categoryArray,
    }));
    
    // Always update Redux filters when URL params change, even if empty
    // This ensures the filter is properly cleared when navigating away
    // Reset to page 0 when category or search changes from URL params
    // Only reset page if the values actually changed to avoid unnecessary resets
    const currentCategory = filters.category || '';
    const currentSearch = filters.search || '';
    
    // Only update if values actually changed to prevent unnecessary dispatches
    if (category !== currentCategory || finalSearch !== currentSearch) {
      dispatch(setCurrentPage(0));
    }
    
    // Always sync Redux filters with URL params (handles initial mount and navigation)
    // This ensures filters are set before product fetching
    dispatch(setFilters({ search: finalSearch, category }));
    
    console.log('🔄 URL Params Sync:', {
      urlSearch: search,
      urlCategory: categoryParam,
      routerSearch,
      routerCategory,
      finalSearch,
      finalCategory,
      normalizedCategory: category,
      currentReduxSearch: currentSearch,
      currentReduxCategory: currentCategory,
    });
    
    // If featured parameter is true, fetch featured products instead of regular products
    if (featured) {
      dispatch(fetchFeaturedProducts({ size: 50 }));
    }
    // Note: Product fetching will happen automatically via the useEffect that depends on filters and searchParams
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString(), dispatch, parseCategory, getUrlParams, urlKey]);

  // Fetch customer wishlist when user is available
  useEffect(() => {
    if (user?.id) {
      dispatch(fetchCustomerWishlist(user.id));
    }
  }, [dispatch, user?.id]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
    };
  }, [searchDebounceTimer]);

  // Handle category checkbox changes
  const handleCategoryChange = (categoryName, checked) => {
    let newSelectedCategories;
    if (checked) {
      newSelectedCategories = [...localFilters.selectedCategories, categoryName];
    } else {
      // Case-insensitive: URL sync title-cases categories ("Kota stone" -> "Kota Stone")
      newSelectedCategories = localFilters.selectedCategories.filter(cat => cat.toLowerCase() !== categoryName.toLowerCase());
    }
    
    const newFilters = {
      ...localFilters,
      selectedCategories: newSelectedCategories,
      category: newSelectedCategories.join(','), // For backend compatibility
    };
    
    setLocalFilters(newFilters);
    autoApplyFilters(newFilters);
  };

  // Auto-apply filters function
  const autoApplyFilters = (newFilters) => {
    dispatch(setFilters(newFilters));
    // Filtered results start from the first page
    dispatch(setCurrentPage(0));
    
    // Update URL params
    const newParams = new URLSearchParams();
    if (newFilters.search) newParams.set('search', newFilters.search);
    if (newFilters.selectedCategories && newFilters.selectedCategories.length > 0) {
      newParams.set('category', newFilters.selectedCategories.join(','));
    }
    setSearchParams(newParams);
    
    setFiltersOpen(false);
  };

  const handleFilterChange = (filterName, value) => {
    const newFilters = {
      ...localFilters,
      [filterName]: value,
    };

    // The category dropdown drives the URL "category" param via selectedCategories;
    // without this the URL sync effect would reset the chosen category back to empty.
    if (filterName === 'category') {
      newFilters.selectedCategories = value ? [value] : [];
    }
    
    setLocalFilters(newFilters);

    // Price slider: onChange only moves the thumbs; the range is applied once on release
    // (handlePriceRangeCommit) so a single, final minPrice/maxPrice is sent to the API.
    if (filterName === 'priceRange' || filterName === 'quantityRange') {
      return;
    }
    
    // For search input, use debouncing to prevent excessive API calls
    if (filterName === 'search') {
      // Clear existing timer
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
      
      // Set new timer for 500ms delay
      const timer = setTimeout(() => {
        autoApplyFilters(newFilters);
      }, 500);
      
      setSearchDebounceTimer(timer);
    } else {
      // For all other filters, apply immediately
      autoApplyFilters(newFilters);
    }
  };
  
  const handlePriceRangeCommit = (value) => {
    const newFilters = { ...localFilters, priceRange: value };
    setPriceRangeExplicitlySet(true);
    setLocalFilters(newFilters);
    autoApplyFilters(newFilters);
  };

  const handleQuantityRangeCommit = (value) => {
    const newFilters = { ...localFilters, quantityRange: value };
    setQuantityRangeExplicitlySet(true);
    setLocalFilters(newFilters);
    autoApplyFilters(newFilters);
  };

  const applyFilters = () => {
    autoApplyFilters(localFilters);
  };
  
  const clearFilters = () => {
    const clearedFilters = {
      search: '',
      category: '',
      selectedCategories: [],
      priceRange: [PRICE_MIN, PRICE_MAX], // Full range so no price filter is applied
      quantityRange: [QUANTITY_MIN, QUANTITY_MAX], // Full range so no quantity filter is applied
      material: '',
      finish: '',
      origin: '',
      status: '',
      approvalStatus: '',
      discountFilter: '',
      sortBy: 'createdAt',
      sortDir: 'desc',
    };
    setPriceRangeExplicitlySet(false); // Reset the flag when clearing filters
    setQuantityRangeExplicitlySet(false);
    setLocalFilters(clearedFilters);
    dispatch(setFilters(clearedFilters));
    setSearchParams({});
  };
  
  const handleAddToCart = useCallback(
    (product) => {
      const quantity = 1;
      dispatch(addToCart({ product, quantity }));
      toast.success(`${product.name} added to cart!`);
    },
    [dispatch]
  );

  const handleRequestVisit = useCallback(
    (product) => {
      if (!user?.id) {
        toast.error('Please login to request a visit');
        return;
      }
      setSelectedProduct(product);
      setVisitDialogOpen(true);
    },
    [user]
  );

  const handleBookALot = useCallback(
    (product) => {
      if (!user?.id) {
        toast.error('Please login to book a lot');
        return;
      }
      setSelectedLotProduct(product);
      setBookLotDialogOpen(true);
    },
    [user]
  );

  const handleWishlistToggle = useCallback(
    async (product, alreadyInWishlist) => {
      if (!user?.id) {
        navigate('/login', { state: { from: { pathname: '/products' } } });
        return;
      }

      try {
        if (alreadyInWishlist) {
          await dispatch(removeFromWishlist({ customerId: user.id, productId: product.id })).unwrap();
          toast.info(`${product.name} removed from wishlist`);
        } else {
          await dispatch(addToWishlist({ customerId: user.id, productId: product.id })).unwrap();
          toast.success(`${product.name} added to wishlist!`);
        }
        dispatch(fetchCustomerWishlist(user.id));
      } catch (err) {
        toast.error(err?.message || 'Failed to update wishlist');
      }
    },
    [dispatch, navigate, user]
  );
  
  const handlePageChange = (event, page) => {
    const newPage = page - 1; // MUI Pagination is 1-based, our state is 0-based
    dispatch(setCurrentPage(newPage));
  };
  
  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      dispatch(setViewMode(newViewMode));
    }
  };
  
  const categories = ['Imported Marble', 'Marble', 'Granite', 'Tiles', 'Sandstone', 'Indian Marble', 'Quartz', 'Slate', 'Kota stone', 'Quartzite'];
  const materials = ['Natural Stone', 'Engineered Stone', 'Ceramic', 'Porcelain'];
  const finishes = ['Polish', 'Unpolish', 'Leather', 'Honed', 'Shot blast', 'Flamed', 'Bush hammered', 'Ribbed', 'Rivera', 'Hydro', 'Flute', 'Rustic', 'Carving', 'Matt', 'CNC'];
  const origins = ['India', 'Turkey','Italy','Spain','Portugal','Greece','Namibia','Iran','China','Brazil','Norway'];
  const productStatusOptions = [
    '',
    'DRAFT',
    'ACTIVE',
    'INACTIVE',
    'OUT_OF_STOCK',
    'DISCONTINUED',
    'REJECTED',
    'SUSPENDED',
  ];
  const approvalStatusOptions = [
    '',
    'PENDING',
    'APPROVED',
    'REJECTED',
    'UNDER_REVIEW',
    'REQUIRES_DOCUMENTS',
    'SUSPENDED',
  ];
  
  const wishlistItems = useMemo(() => wishlist?.items || [], [wishlist?.items]);
  const wishlistProductIds = useMemo(
    () => new Set(wishlistItems.map((item) => item.productId)),
    [wishlistItems]
  );
  
  const FilterPanel = () => (
    <Paper sx={{ p: 3, height: 'fit-content' }}>
      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <FilterList sx={{ mr: 1 }} />
        Filters
        <Button
          size="small"
          onClick={clearFilters}
          sx={{ ml: 'auto' }}
          startIcon={<Clear />}
        >
          Clear All
        </Button>
      </Typography>
      
      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Search Products"
          value={localFilters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
          }}
        />
      </Box>
      
      {/* Category */}
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Category</InputLabel>
          <Select
            value={categories.find(c => c.toLowerCase() === (localFilters.category || '').toLowerCase()) || ''}
            label="Category"
            onChange={(e) => handleFilterChange('category', e.target.value)}
          >
            <MenuItem value="">All Categories</MenuItem>
            {categories.map((category) => (
              <MenuItem key={`category-${category}`} value={category}>
                {category}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      {/* Price Range */}
      <Box sx={{ mb: 3, p: 2, border: '1px solid #e5e7eb', borderRadius: 3, bgcolor: '#fff' }}>
        <PriceRangeFilter
          value={boundedPriceRange}
          prices={priceFilterPrices}
          loading={priceListLoading}
          onChange={(range) => handleFilterChange('priceRange', range)}
          onCommit={handlePriceRangeCommit}
        />
      </Box>

      {/* Quantity (available SFT) */}
      <Box sx={{ mb: 3, p: 2, border: '1px solid #e5e7eb', borderRadius: 3, bgcolor: '#fff' }}>
        <QuantityRangeFilter
          value={boundedQuantityRange}
          quantities={quantityFilterValues}
          loading={quantityListLoading}
          onChange={(range) => handleFilterChange('quantityRange', range)}
          onCommit={handleQuantityRangeCommit}
        />
      </Box>
      
      {/* Material */}
      {/* <Box sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Material</InputLabel>
          <Select
            value={localFilters.material}
            label="Material"
            onChange={(e) => handleFilterChange('material', e.target.value)}
          >
            <MenuItem value="">All Materials</MenuItem>
            {materials.map((material) => (
              <MenuItem key={`material-${material}`} value={material}>
                {material}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box> */}
      
      {/* Finish */}
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Finish</InputLabel>
          <Select
            value={localFilters.finish}
            label="Finish"
            onChange={(e) => handleFilterChange('finish', e.target.value)}
          >
            <MenuItem value="">All Finishes</MenuItem>
            {finishes.map((finish) => (
              <MenuItem key={`finish-${finish}`} value={finish}>
                {finish}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      {/* Origin */}
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Origin</InputLabel>
          <Select
            value={localFilters.origin}
            label="Origin"
            onChange={(e) => handleFilterChange('origin', e.target.value)}
          >
            <MenuItem value="">All Origins</MenuItem>
            {origins.map((origin) => (
              <MenuItem key={`origin-${origin}`} value={origin}>
                {origin}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      {/* Discount Filter */}
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Discount</InputLabel>
          <Select
            value={localFilters.discountFilter}
            label="Discount"
            onChange={(e) => handleFilterChange('discountFilter', e.target.value)}
          >
            <MenuItem value="">All Products</MenuItem>
            <MenuItem value="10-20">10% - 20% OFF</MenuItem>
            <MenuItem value="20-30">20% - 30% OFF</MenuItem>
            <MenuItem value="30-40">30% - 40% OFF</MenuItem>
            <MenuItem value="40-50">40% - 50% OFF</MenuItem>
            <MenuItem value="50+">50%+ OFF</MenuItem>
            <MenuItem value="amount-500">₹500+ OFF</MenuItem>
            <MenuItem value="amount-1000">₹1000+ OFF</MenuItem>
            <MenuItem value="amount-2000">₹2000+ OFF</MenuItem>
            <MenuItem value="amount-5000">₹5000+ OFF</MenuItem>
            <MenuItem value="any-discount">Any Discount</MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      {isAdmin && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Admin Filters</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Product Status</InputLabel>
                <Select
                  value={localFilters.status}
                  label="Product Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  {productStatusOptions.map((s) => (
                    <MenuItem key={`status-${s || 'ALL'}`} value={s}>
                      {s || 'All Statuses'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Approval Status</InputLabel>
                <Select
                  value={localFilters.approvalStatus}
                  label="Approval Status"
                  onChange={(e) => handleFilterChange('approvalStatus', e.target.value)}
                >
                  {approvalStatusOptions.map((s) => (
                    <MenuItem key={`approval-${s || 'ALL'}`} value={s}>
                      {s || 'All Approvals'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      )}
      
      {/* Sort */}
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={`${localFilters.sortBy}-${localFilters.sortDir}`}
            label="Sort By"
            onChange={(e) => {
              const [sortBy, sortDir] = e.target.value.split('-');
              const newFilters = {
                ...localFilters,
                sortBy,
                sortDir,
              };
              setLocalFilters(newFilters);
              autoApplyFilters(newFilters);
            }}
          >
            <MenuItem value="createdAt-desc">Newest First</MenuItem>
            <MenuItem value="createdAt-asc">Oldest First</MenuItem>
            <MenuItem value="price-asc">Price: Low to High</MenuItem>
            <MenuItem value="price-desc">Price: High to Low</MenuItem>
            <MenuItem value="name-asc">Name: A to Z</MenuItem>
            <MenuItem value="name-desc">Name: Z to A</MenuItem>
          </Select>
        </FormControl>
      </Box>
      

    </Paper>
  );
  
  // Filter products based on discount filter
  const filteredProductsByDiscount = useMemo(() => {
    if (!products?.length || !localFilters.discountFilter) {
      return products || [];
    }

    return products.filter((product) => {
      const discountFilter = localFilters.discountFilter;
      let promotion = getBestPromotionForProduct(product.id, product.category, products);

      // No promotion: fall back to the seller's discountedPrice (same price the card shows)
      if (!promotion) {
        const base = Number(product.price) || 0;
        const discounted = Number(product.discountedPrice) || 0;
        if (base <= 0 || discounted <= 0 || discounted >= base) {
          return false; // no discount at all
        }
        promotion = discountFilter.startsWith('amount-')
          ? { discountType: 'amount', discountAmount: base - discounted }
          : { discountType: 'percentage', discount: ((base - discounted) / base) * 100 };
      }

      // Handle "Any Discount" filter - show products with any active promotion
      if (discountFilter === 'any-discount') {
        return true;
      }

      // Handle percentage discounts
      if (promotion.discountType === 'percentage' && promotion.discount) {
        const discountPercent = Number(promotion.discount);
        if (discountFilter === '10-20') return discountPercent >= 10 && discountPercent <= 20;
        if (discountFilter === '20-30') return discountPercent >= 20 && discountPercent <= 30;
        if (discountFilter === '30-40') return discountPercent >= 30 && discountPercent <= 40;
        if (discountFilter === '40-50') return discountPercent >= 40 && discountPercent <= 50;
        if (discountFilter === '50+') return discountPercent >= 50;
      }

      // Handle amount discounts
      if (promotion.discountType === 'amount' && promotion.discountAmount) {
        const discountAmount = Number(promotion.discountAmount);
        if (discountFilter === 'amount-500') return discountAmount >= 500;
        if (discountFilter === 'amount-1000') return discountAmount >= 1000;
        if (discountFilter === 'amount-2000') return discountAmount >= 2000;
        if (discountFilter === 'amount-5000') return discountAmount >= 5000;
      }

      // If promotion type doesn't match filter type, don't show
      return false;
    });
  }, [products, localFilters.discountFilter]);

  const productCards = useMemo(() => {
    const productsToDisplay = filteredProductsByDiscount || [];
    if (!productsToDisplay.length) {
      return [];
    }

    return productsToDisplay.map((product, index) => {
      const key = product.id || `product-${index}`;
      const isInWishlist = product.id ? wishlistProductIds.has(product.id) : false;

      return (
        <ProductCard
          key={`${key}-${promotionUpdateKey}`}
          product={product}
          isInWishlist={isInWishlist}
          onWishlistToggle={handleWishlistToggle}
          onRequestVisit={handleRequestVisit}
          onBookLot={handleBookALot}
          onAddToCart={handleAddToCart}
          allProducts={productsToDisplay}
        />
      );
    });
  }, [filteredProductsByDiscount, wishlistProductIds, handleWishlistToggle, handleRequestVisit, handleBookALot, handleAddToCart, promotionUpdateKey]);
  
  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Collapsed Sidebar Button - Fixed to left side */}
      {sidebarCollapsed && (
        <Box
          onClick={() => setSidebarCollapsed(false)}
          sx={{
            position: 'fixed',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: '56px',
            height: '160px',
            bgcolor: '#fff',
            border: '2px solid #e5e7eb',
            borderLeft: 'none',
            borderRadius: '0 28px 28px 0',
            boxShadow: '4px 0 24px rgba(0, 0, 0, 0.08)',
            cursor: 'pointer',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            transition: 'all 0.3s ease',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '4px',
              background: 'linear-gradient(180deg, #2C3E50 0%, #2C3E50 100%)',
            },
            '&:hover': {
              borderColor: '#2C3E50',
              transform: 'translateY(-50%) translateX(8px)',
              boxShadow: '0 0 20px rgba(44, 62, 80, 0.3), 4px 0 24px rgba(0, 0, 0, 0.12)',
            }
          }}
        >
          {/* Three horizontal lines (filter icon) with gradient */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', mb: 0.5 }}>
            <Box sx={{ 
              width: '20px', 
              height: '2px', 
              background: 'linear-gradient(90deg, #2C3E50 0%, #34495e 100%)',
              borderRadius: '1px'
            }} />
            <Box sx={{ 
              width: '20px', 
              height: '2px', 
              background: 'linear-gradient(90deg, #2C3E50 0%, #34495e 100%)',
              borderRadius: '1px'
            }} />
            <Box sx={{ 
              width: '20px', 
              height: '2px', 
              background: 'linear-gradient(90deg, #2C3E50 0%, #34495e 100%)',
              borderRadius: '1px'
            }} />
          </Box>
          {/* Vertical "FILTERS" text */}
          <Typography
            sx={{
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              fontSize: '10px',
              fontWeight: 700,
              color: '#2C3E50',
              letterSpacing: '1px',
              textTransform: 'uppercase',
            }}
          >
            FILTERS
          </Typography>
        </Box>
      )}

      {/* Expanded Sidebar */}
      {!sidebarCollapsed && (
        <Box sx={{
          width: '380px',
          height: '100vh',
          bgcolor: 'white',
          borderRight: '3px solid #000',
          boxShadow: '8px 0 0 #000',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          transition: 'transform 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
          overflow: 'hidden',
          '&::-webkit-scrollbar': {
            width: '10px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f3f4f6',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'linear-gradient(180deg, #2C3E50 0%, #34495e 100%)',
            borderRadius: '5px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'linear-gradient(180deg, #1a252f 0%, #2C3E50 100%)',
          },
        }}>
          {/* Sidebar Header */}
          <Box sx={{ 
            padding: '24px 28px 0 28px',
            borderBottom: 'none',
          }}>
            <Box sx={{ 
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              mb: 2
            }}>
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: '36px',
                    fontWeight: 900,
                    color: '#000',
                    textTransform: 'uppercase',
                    mb: 1,
                    lineHeight: 1
                  }}
                >
                  FILTERS
                </Typography>
                {/* Underline gradient bar */}
                <Box sx={{
                  width: '60px',
                  height: '6px',
                  background: 'linear-gradient(90deg, #2C3E50 0%, #34495e 100%)',
                  borderRadius: '3px',
                  mb: 1
                }} />
                {/* Product Count */}
                <Typography 
                  sx={{ 
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#6b7280',
                    mt: 0.5
                  }}
                >
                  {effectiveTotalElements} {effectiveTotalElements === 1 ? 'product' : 'products'} found
                </Typography>
              </Box>
              {/* Close button */}
              <IconButton
                onClick={() => setSidebarCollapsed(true)}
                sx={{
                  width: '44px',
                  height: '44px',
                  bgcolor: '#fff',
                  border: '3px solid #000',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  ml: 2,
                  flexShrink: 0,
                  '&:hover': {
                    transform: 'rotate(90deg)',
                    bgcolor: '#2C3E50',
                    borderColor: '#2C3E50',
                    '& .MuiSvgIcon-root': {
                      color: '#fff',
                    }
                  }
                }}
              >
                <Clear sx={{ fontSize: 20, color: '#000' }} />
              </IconButton>
            </Box>
            {/* Clear All Button */}
            <Button
              onClick={clearFilters}
              sx={{
                mt: 2,
                textTransform: 'uppercase',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '1px',
                color: '#6b7280',
                '&:hover': {
                  color: '#2C3E50',
                  bgcolor: 'transparent',
                }
              }}
              startIcon={<Clear sx={{ fontSize: 16 }} />}
            >
              Clear All Filters
            </Button>
          </Box>

          {/* Filter Content - Scrollable */}
          <Box sx={{ 
            flex: 1, 
            padding: '0 28px 40px 28px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '10px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f3f4f6',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'linear-gradient(180deg, #2C3E50 0%, #34495e 100%)',
              borderRadius: '5px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'linear-gradient(180deg, #1a252f 0%, #2C3E50 100%)',
            },
          }}>
            {/* Search Input */}
            <Box sx={{ 
              mb: 2,
              bgcolor: '#f9fafb',
              border: '2px solid #e5e7eb',
              borderRadius: '16px',
              padding: '16px',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#2C3E50',
                boxShadow: '0 0 0 3px rgba(236, 72, 153, 0.1)',
              }
            }}>
              <Typography 
                sx={{ 
                  fontSize: '12px',
                  fontWeight: 800,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  mb: 1
                }}
              >
                SEARCH
              </Typography>
              <TextField
                fullWidth
                placeholder="Search products..."
                value={localFilters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: '#fff',
                    '& fieldset': {
                      borderColor: '#e5e7eb',
                    },
                    '&:hover fieldset': {
                      borderColor: '#d1d5db',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2C3E50',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '10px 14px',
                  }
                }}
              />
            </Box>

            {/* Category Dropdown */}
            <Box sx={{ 
              mb: 2,
              bgcolor: '#f9fafb',
              border: '2px solid #e5e7eb',
              borderRadius: '16px',
              padding: '16px',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#2C3E50',
                boxShadow: '0 0 0 3px rgba(236, 72, 153, 0.1)',
              }
            }}>
              <Typography 
                sx={{ 
                  fontSize: '12px',
                  fontWeight: 800,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  mb: 1
                }}
              >
                CATEGORIES
              </Typography>
              <Accordion 
                sx={{ 
                  boxShadow: 'none',
                  bgcolor: 'transparent',
                  border: 'none',
                  '&:before': { display: 'none' },
                  '& .MuiAccordionSummary-root': {
                    minHeight: 'auto',
                    padding: '0',
                    bgcolor: '#fff',
                    borderRadius: '8px',
                    border: '1px solid #2C3E50',
                    '&.Mui-expanded': {
                      minHeight: 'auto',
                    }
                  },
                  '& .MuiAccordionDetails-root': {
                    padding: '12px 0 0 0',
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    px: 1.5,
                    py: 1,
                    '& .MuiAccordionSummary-content': {
                      margin: '0',
                      '&.Mui-expanded': {
                        margin: '0',
                      }
                    }
                  }}
                >
                  <Typography sx={{ 
                    fontSize: '14px',
                    color: localFilters.selectedCategories.length > 0 ? '#1f2937' : '#6b7280',
                    fontWeight: 500
                  }}>
                    {localFilters.selectedCategories.length > 0 
                      ? `${localFilters.selectedCategories.length} selected`
                      : 'Select Categories'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                {/* Selected Categories Display */}
                {localFilters.selectedCategories.length > 0 && (
                  <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {localFilters.selectedCategories.map((category) => (
                      <Chip
                        key={`selected-${category}`}
                        label={category}
                        size="small"
                        onDelete={() => handleCategoryChange(category, false)}
                        sx={{
                          bgcolor: '#e3f2fd',
                          color: '#1976d2',
                          '& .MuiChip-deleteIcon': {
                            color: '#1976d2',
                          }
                        }}
                      />
                    ))}
                    <Button
                      size="small"
                      onClick={() => {
                        const newFilters = {
                          ...localFilters,
                          selectedCategories: [],
                          category: '',
                        };
                        setLocalFilters(newFilters);
                        autoApplyFilters(newFilters);
                      }}
                      sx={{ 
                        color: 'text.secondary',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        minWidth: 'auto',
                        p: 0.5
                      }}
                    >
                      Clear All
                    </Button>
                  </Box>
                )}
                
                {/* Category Checkboxes */}
                <FormGroup>
                  {categories.map((category) => (
                    <FormControlLabel
                      key={`checkbox-${category}`}
                      control={
                        <Checkbox
                          checked={localFilters.selectedCategories.some(c => c.toLowerCase() === category.toLowerCase())}
                          onChange={(e) => handleCategoryChange(category, e.target.checked)}
                          size="small"
                          sx={{
                            color: '#666',
                            '&.Mui-checked': {
                              color: '#1976d2',
                            }
                          }}
                        />
                      }
                      label={category}
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.875rem',
                          color: 'text.primary'
                        }
                      }}
                    />
                  ))}
                </FormGroup>
              </AccordionDetails>
            </Accordion>
          </Box>

            {/* Price Range */}
            <Box sx={{ 
              mb: 2,
              bgcolor: '#f9fafb',
              border: '2px solid #e5e7eb',
              borderRadius: '16px',
              padding: '16px',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#2C3E50',
                boxShadow: '0 0 0 3px rgba(236, 72, 153, 0.1)',
              }
            }}>
              <PriceRangeFilter
                value={boundedPriceRange}
                prices={priceFilterPrices}
                loading={priceListLoading}
                onChange={(range) => handleFilterChange('priceRange', range)}
                onCommit={handlePriceRangeCommit}
              />
            </Box>

            {/* Quantity (available SFT) */}
            <Box sx={{
              mb: 2,
              bgcolor: '#f9fafb',
              border: '2px solid #e5e7eb',
              borderRadius: '16px',
              padding: '16px',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#2C3E50',
                boxShadow: '0 0 0 3px rgba(236, 72, 153, 0.1)',
              }
            }}>
              <QuantityRangeFilter
                value={boundedQuantityRange}
                quantities={quantityFilterValues}
                loading={quantityListLoading}
                onChange={(range) => handleFilterChange('quantityRange', range)}
                onCommit={handleQuantityRangeCommit}
              />
            </Box>

          {/* Material */}
          {/* <Box sx={{ mb: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Material</InputLabel>
              <Select
                value={localFilters.material}
                label="Material"
                onChange={(e) => handleFilterChange('material', e.target.value)}
                sx={{
                  borderRadius: '6px',
                  '& .MuiSelect-select': {
                    padding: '10px',
                  }
                }}
              >
                <MenuItem value="">All Materials</MenuItem>
                {materials.map((material) => (
                  <MenuItem key={`material-${material}`} value={material}>
                    {material}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box> */}

            {/* Finish */}
            <Box sx={{ 
              mb: 2,
              bgcolor: '#f9fafb',
              border: '2px solid #e5e7eb',
              borderRadius: '16px',
              padding: '16px',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#2C3E50',
                boxShadow: '0 0 0 3px rgba(236, 72, 153, 0.1)',
              }
            }}>
              <Typography 
                sx={{ 
                  fontSize: '12px',
                  fontWeight: 800,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  mb: 1
                }}
              >
                FINISH
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={localFilters.finish}
                  displayEmpty
                  onChange={(e) => handleFilterChange('finish', e.target.value)}
                  sx={{
                    borderRadius: '8px',
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                    '& .MuiSelect-select': {
                      padding: '10px 14px',
                    },
                    '&:hover': {
                      borderColor: '#2C3E50',
                    },
                    '&.Mui-focused': {
                      borderColor: '#2C3E50',
                    }
                  }}
                >
                  <MenuItem value="">Select Finish</MenuItem>
                  {finishes.map((finish) => (
                    <MenuItem key={`finish-${finish}`} value={finish}>
                      {finish}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Origin */}
            <Box sx={{ 
              mb: 2,
              bgcolor: '#f9fafb',
              border: '2px solid #e5e7eb',
              borderRadius: '16px',
              padding: '16px',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#2C3E50',
                boxShadow: '0 0 0 3px rgba(236, 72, 153, 0.1)',
              }
            }}>
              <Typography 
                sx={{ 
                  fontSize: '12px',
                  fontWeight: 800,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  mb: 1
                }}
              >
                ORIGIN
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={localFilters.origin}
                  displayEmpty
                  onChange={(e) => handleFilterChange('origin', e.target.value)}
                  sx={{
                    borderRadius: '8px',
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                    '& .MuiSelect-select': {
                      padding: '10px 14px',
                    },
                    '&:hover': {
                      borderColor: '#d1d5db',
                    },
                    '&.Mui-focused': {
                      borderColor: '#2C3E50',
                    }
                  }}
                >
                  <MenuItem value="">Select Origin</MenuItem>
                  {origins.map((origin) => (
                    <MenuItem key={`origin-${origin}`} value={origin}>
                      {origin}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Discount Filter */}
            <Box sx={{ 
              mb: 2,
              bgcolor: '#f9fafb',
              border: '2px solid #e5e7eb',
              borderRadius: '16px',
              padding: '16px',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#2C3E50',
                boxShadow: '0 0 0 3px rgba(236, 72, 153, 0.1)',
              }
            }}>
              <Typography 
                sx={{ 
                  fontSize: '12px',
                  fontWeight: 800,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  mb: 1
                }}
              >
                DISCOUNT
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={localFilters.discountFilter}
                  displayEmpty
                  onChange={(e) => handleFilterChange('discountFilter', e.target.value)}
                  sx={{
                    borderRadius: '8px',
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                    '& .MuiSelect-select': {
                      padding: '10px 14px',
                    },
                    '&:hover': {
                      borderColor: '#d1d5db',
                    },
                    '&.Mui-focused': {
                      borderColor: '#2C3E50',
                    }
                  }}
                >
                  <MenuItem value="">All Products</MenuItem>
                  <MenuItem value="10-20">10% - 20% OFF</MenuItem>
                  <MenuItem value="20-30">20% - 30% OFF</MenuItem>
                  <MenuItem value="30-40">30% - 40% OFF</MenuItem>
                  <MenuItem value="40-50">40% - 50% OFF</MenuItem>
                  <MenuItem value="50+">50%+ OFF</MenuItem>
                  <MenuItem value="amount-500">₹500+ OFF</MenuItem>
                  <MenuItem value="amount-1000">₹1000+ OFF</MenuItem>
                  <MenuItem value="amount-2000">₹2000+ OFF</MenuItem>
                  <MenuItem value="amount-5000">₹5000+ OFF</MenuItem>
                  <MenuItem value="any-discount">Any Discount</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Admin Filters */}
            {isAdmin && (
              <Box sx={{ 
                mb: 2,
                bgcolor: '#f9fafb',
                border: '2px solid #e5e7eb',
                borderRadius: '16px',
                padding: '16px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#ec4899',
                  boxShadow: '0 0 0 3px rgba(236, 72, 153, 0.1)',
                }
              }}>
                <Typography 
                  sx={{ 
                    fontSize: '12px',
                    fontWeight: 800,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '2px',
                    mb: 1
                  }}
                >
                  ADMIN FILTERS
                </Typography>
                
                {/* Product Status */}
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={localFilters.status}
                      displayEmpty
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      sx={{
                        borderRadius: '8px',
                        bgcolor: '#fff',
                        border: '1px solid #e5e7eb',
                        '& .MuiSelect-select': {
                          padding: '10px 14px',
                        },
                        '&:hover': {
                          borderColor: '#d1d5db',
                        },
                        '&.Mui-focused': {
                          borderColor: '#2C3E50',
                        }
                      }}
                    >
                      {productStatusOptions.map((s) => (
                        <MenuItem key={`status-${s || 'ALL'}`} value={s}>
                          {s || 'All Statuses'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Approval Status */}
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={localFilters.approvalStatus}
                      displayEmpty
                      onChange={(e) => handleFilterChange('approvalStatus', e.target.value)}
                      sx={{
                        borderRadius: '8px',
                        bgcolor: '#fff',
                        border: '1px solid #e5e7eb',
                        '& .MuiSelect-select': {
                          padding: '10px 14px',
                        },
                        '&:hover': {
                          borderColor: '#2C3E50',
                        },
                        '&.Mui-focused': {
                          borderColor: '#2C3E50',
                        }
                      }}
                    >
                      {approvalStatusOptions.map((s) => (
                        <MenuItem key={`approval-${s || 'ALL'}`} value={s}>
                          {s || 'All Approvals'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}

            {/* Sort By */}
            <Box sx={{ 
              mb: 2,
              bgcolor: '#f9fafb',
              border: '2px solid #e5e7eb',
              borderRadius: '16px',
              padding: '16px',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#2C3E50',
                boxShadow: '0 0 0 3px rgba(236, 72, 153, 0.1)',
              }
            }}>
              <Typography 
                sx={{ 
                  fontSize: '12px',
                  fontWeight: 800,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  mb: 1
                }}
              >
                SORT BY
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={`${localFilters.sortBy}-${localFilters.sortDir}`}
                  onChange={(e) => {
                    const [sortBy, sortDir] = e.target.value.split('-');
                    const newFilters = {
                      ...localFilters,
                      sortBy,
                      sortDir,
                    };
                    setLocalFilters(newFilters);
                    autoApplyFilters(newFilters);
                  }}
                  sx={{
                    borderRadius: '8px',
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                    '& .MuiSelect-select': {
                      padding: '10px 14px',
                    },
                    '&:hover': {
                      borderColor: '#d1d5db',
                    },
                    '&.Mui-focused': {
                      borderColor: '#2C3E50',
                    }
                  }}
                >
                  <MenuItem value="createdAt-desc">Newest First</MenuItem>
                  <MenuItem value="createdAt-asc">Oldest First</MenuItem>
                  <MenuItem value="price-asc">Price: Low to High</MenuItem>
                  <MenuItem value="price-desc">Price: High to Low</MenuItem>
                  <MenuItem value="name-asc">Name: A to Z</MenuItem>
                  <MenuItem value="name-desc">Name: Z to A</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>
      )}

      {/* Right Content - Scrollable Product Grid */}
      <Box sx={{
        height: '100vh',
        overflow: 'auto',
        bgcolor: '#f8f9fa',
        flex: 1,
        marginLeft: sidebarCollapsed ? 0 : 0,
        transition: 'margin-left 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
      }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* AI Search Message */}
        {aiSearchMessage && (
          <Alert 
            severity={aiSearchResults?.main_product?.length > 0 ? "success" : "info"} 
            sx={{ m: 2 }}
          >
            {aiSearchMessage}
          </Alert>
        )}

        {/* Suggested Products from AI Search */}
        {aiSearchResults?.suggested_product && aiSearchResults.suggested_product.length > 0 && (
          <Box sx={{ m: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Suggested Products:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {aiSearchResults.suggested_product.map((suggestion, index) => (
                <Chip
                  key={index}
                  label={suggestion.name?.replace(/<[^>]*>/g, '') || suggestion.name}
                  onClick={() => {
                    const productName = suggestion.name?.replace(/<[^>]*>/g, '') || suggestion.name;
                    navigate(`/products?ai_search=${encodeURIComponent(productName)}`);
                  }}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Product Grid Container */}
        <Box sx={{ p: 2}}>
          {loading ? (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, 320px)',
              gap: 2,
              justifyContent: 'center'
            }}>
              {[...Array(8)].map((_, index) => (
                <Card key={index} sx={{ height: '320px', width: '320px', borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
                  <Skeleton variant="rectangular" height={200} />
                  <CardContent>
                    <Skeleton variant="text" height={32} />
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} width="60%" />
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (filteredProductsByDiscount?.length === 0 || productCards.length === 0) ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
                No products found
              </Typography>
              {localFilters.discountFilter && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No products match the selected discount filter. Try a different filter or clear filters to see all products.
                </Typography>
              )}
              <Button
                variant="outlined"
                onClick={clearFilters}
                sx={{ mt: 2 }}
              >
                Clear Filters
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, 320px)',
                gap: 2,
                justifyContent: 'center'
              }}>
                {productCards}
              </Box>
              
              {/* Pagination */}
              {effectiveTotalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, pb: 2 }}>
                  <Pagination
                    count={effectiveTotalPages}
                    page={effectiveCurrentPage + 1}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Mobile Filters Drawer */}
      <Drawer
        anchor="right"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        sx={{ display: { md: 'none' } }}
        PaperProps={{
          sx: { width: 320, p: 2 },
        }}
      >
        {FilterPanel()}
      </Drawer>

      {/* Visit Request Dialog */}
      <VisitRequestDialog
        open={visitDialogOpen}
        onClose={() => {
          setVisitDialogOpen(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        onSubmit={async (visitData) => {
          try {
            await visitRequestService.createVisitRequest(visitData);
            toast.success('Visit request submitted successfully!');
            setVisitDialogOpen(false);
            setSelectedProduct(null);
          } catch (error) {
            console.error('Error submitting visit request:', error);
            toast.error('Failed to submit visit request. Please try again.');
          }
        }}
      />

      {/* Book a Lot Dialog */}
      <BookLotDialog
        open={bookLotDialogOpen}
        onClose={() => {
          setBookLotDialogOpen(false);
          setSelectedLotProduct(null);
        }}
        product={selectedLotProduct}
        user={user}
      />
    </Box>
  );
};

export default ProductsPage;